// Bitcoin power-law model — Santostasi / Burger formulation.
// Coefficients pinned to match the macro dashboard's PowerLawNavigator so
// the two products tell the same story about fair value. Read as:
//
//   price = 10^coef × days_since_genesis^exp
//
// `fair` is the headline curve. `floor` and `ceiling` define a support /
// resistance band — projections always show the band, never just `fair`,
// so users don't read a single number as a prediction.

export const GENESIS = new Date('2009-01-03T00:00:00Z');

export const POWER_LAW_MODEL = {
  floor:   { coef: -17.668, exp: 5.84 },
  fair:    { coef: -17.010, exp: 5.84 },
  ceiling: { coef: -16.344, exp: 5.84 },
};

const MS_PER_DAY = 86_400_000;

export function daysSinceGenesis(date = new Date()) {
  return Math.max(1, Math.floor((date.getTime() - GENESIS.getTime()) / MS_PER_DAY));
}

function applyPowerLaw({ coef, exp }, days) {
  return Math.pow(10, coef) * Math.pow(days, exp);
}

export function powerLawFairValue(date = new Date()) {
  return applyPowerLaw(POWER_LAW_MODEL.fair, daysSinceGenesis(date));
}

export function powerLawBand(date = new Date()) {
  const days = daysSinceGenesis(date);
  return {
    floor:   applyPowerLaw(POWER_LAW_MODEL.floor, days),
    fair:    applyPowerLaw(POWER_LAW_MODEL.fair, days),
    ceiling: applyPowerLaw(POWER_LAW_MODEL.ceiling, days),
  };
}

// Distance from fair value as a signed percentage. Positive = above fair,
// negative = below. Returns null if inputs are invalid so callers can
// surface "no data" rather than a misleading number.
export function fairValueDistancePct(spot, date = new Date()) {
  if (!Number.isFinite(spot) || spot <= 0) return null;
  const fair = powerLawFairValue(date);
  if (!Number.isFinite(fair) || fair <= 0) return null;
  return ((spot / fair) - 1) * 100;
}

// Years of price history the model is fit on (today minus 2009-01-03).
// Updates daily so the displayed "X years of data" stays honest.
export function modelAgeYears(date = new Date()) {
  return daysSinceGenesis(date) / 365.25;
}

// Implied year-over-year growth rate at a given evaluation date.
//
// Why this isn't constant: the power law is price(t) = K · t^β where t
// is days since genesis. The 12-month-forward growth rate from any
// point t is ((t+365)/t)^β − 1, which shrinks as t grows. Surfacing
// this number directly counters the lazy reading that "BTC compounds
// at the same rate forever" — the model itself says it doesn't.
//
//   atDate      — today by default
//   yearsAhead  — 0 for "right now"; 10 for "the model's projection 10y out"
export function impliedAnnualGrowthRate(atDate = new Date(), yearsAhead = 0) {
  const evalMs = atDate.getTime() + yearsAhead * 365.25 * 86_400_000;
  const t = daysSinceGenesis(new Date(evalMs));
  if (t <= 0) return null;
  return Math.pow((t + 365) / t, POWER_LAW_MODEL.fair.exp) - 1;
}
